import Alpine from 'alpinejs';
import {
  getPartnerIconBackground,
  getPartnerIconUrl,
} from '../utils/partnerIcons';

export default function registerCalendarSettingsChannelsStore() {
  const existing = Alpine.store('calendarSettingsChannelsStore');
  if (existing) {
    return existing;
  }
  const baseStore = Alpine.store('calendarSettingsStore');
  if (!baseStore) return null;

  const customStore = {
    // Channels UI / Channex integration
    channexEnabled: false,
    hasProperty: false,
    isCreatingProperty: false,
    showIframe: false,
    iframeUrl: '',
    statusChecked: false,
    syncProgress: null,
    syncWebSocket: null,
    activeChannelTab: 'partners',
    calendarId: null,
    channelsInitialized: false,
    channelsInitPromise: null,
    loadingIframe: false,

    // Partner management
    partners: [],
    partnersLoading: true,
    partnersSyncing: false,
    showAddPartnerModal: false,
    showEditPartnerModal: false,
    editingPartner: {},
    partnerSaveError: '',
    showAdvancedPartnerSettings: false,
    partnerCountries: [],
    hasInvoicingIntegration: false,
    autoInvoiceEnabled: false,
    otaOptions: [
      { code: 'BOOKING_COM', name: 'Booking.com', commission: 15 },
      { code: 'AIRBNB', name: 'Airbnb', commission: 3 },
      { code: 'HOSTELWORLD', name: 'Hostelworld', commission: 12 },
      { code: 'EXPEDIA', name: 'Expedia', commission: 18 },
      { code: 'HOTELS_COM', name: 'Hotels.com', commission: 18 },
      { code: 'AGODA', name: 'Agoda', commission: 15 },
      { code: 'VRBO', name: 'Vrbo', commission: 8 },
      { code: 'TRIPADVISOR', name: 'TripAdvisor', commission: 15 },
      { code: 'GOOGLE_HOTEL', name: 'Google Hotel Ads', commission: 10 },
      { code: 'PRICELINE', name: 'Priceline', commission: 15 },
      { code: 'MR_AND_MRS_SMITH', name: 'Mr & Mrs Smith', commission: 15 },
      { code: 'CTRIP', name: 'Ctrip', commission: 15 },
      { code: 'TRAVELOKA', name: 'Traveloka', commission: 15 },
      { code: 'HOTELBEDS', name: 'Hotelbeds', commission: 20 },
      { code: 'OTHER_OTA', name: 'Other OTA', commission: 15 },
    ],
    newPartner: {
      name: '',
      partner_type: 'TRAVEL_AGENCY',
      payment_mode: 'FULL_PAYMENT',
      commission_rate: '',
      booking_source_code: '',
      selected_ota: '',
      invoicing_mode: 'CUSTOMER',
      partner_invoice_amount_type: 'FULL',
      collects_commission: false,
      auto_invoice: true,
      guest_email_policy: 'CONFIRMATION_ONLY',
      notification_email: '',
      guest_email_required: true,
      guest_communication_mode: 'INITIAL_ONLY',
      business_name: '',
      vat_number: '',
      billing_address: '',
      billing_city: '',
      billing_postal_code: '',
      billing_country: '',
      billing_email: '',
    },

    async init() {
      if (this.channelsInitialized) return;
      if (this.channelsInitPromise) return this.channelsInitPromise;

      const container = document.getElementById('calendarSettingsContainer');
      this.calendarId = container ? container.dataset.calendarId : null;
      this.hasInvoicingIntegration =
        container?.dataset?.invoicexpressEnabled === 'true';
      this.autoInvoiceEnabled =
        container?.dataset?.autoInvoiceEnabled === 'true';

      this.channelsInitPromise = (async () => {
        try {
          await Promise.allSettled([this.loadPartners(), this.checkStatus()]);
          this.channelsInitialized = true;
        } finally {
          this.channelsInitPromise = null;
        }
      })();

      return this.channelsInitPromise;
    },

    getCsrfToken() {
      const tokenInput = document.querySelector('[name=csrfmiddlewaretoken]');
      if (tokenInput && tokenInput.value) {
        return tokenInput.value;
      }
      const tokenMatch = document.cookie.match(/(?:^|; )csrftoken=([^;]+)/);
      return tokenMatch ? decodeURIComponent(tokenMatch[1]) : '';
    },

    notifyPartner(message, type = 'info') {
      const toastType = type === 'error' ? 'danger' : type;
      if (window.Alpine && Alpine.store('toastStore')) {
        Alpine.store('toastStore').addToast(message, toastType);
      } else {
        console.log(`[${type}] ${message}`);
        if (type === 'error' || type === 'danger') {
          alert(message);
        }
      }
    },

    async fetchPartnerCountries() {
      if (this.partnerCountries.length > 0) return;
      try {
        const response = await fetch('/customers/countries/');
        if (response.ok) {
          const data = await response.json();
          this.partnerCountries = data.countries || [];
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    },

    getPartnerIconUrl(partner) {
      return getPartnerIconUrl(partner);
    },

    getPartnerIconBackground(partner) {
      return getPartnerIconBackground(partner);
    },

    async loadPartners() {
      try {
        this.partnersLoading = true;
        if (!this.calendarId) {
          console.error('Calendar ID not found');
          this.partners = [];
          return;
        }

        const response = await fetch(
          `/partners/api/partners/?calendar_id=${this.calendarId}`,
        );
        if (response.ok) {
          const partners = await response.json();
          this.partners = partners.map((p) => {
            try {
              let partnerInvoiceAmountType = (
                p.partner_invoice_amount_type || 'FULL'
              ).toString();
              if (
                !['FULL', 'FULL_MINUS_COMMISSION'].includes(
                  partnerInvoiceAmountType,
                )
              ) {
                partnerInvoiceAmountType = 'FULL_MINUS_COMMISSION';
              }
              return {
                ...p,
                _previousPaymentMode: p.payment_mode || 'FULL_PAYMENT',
                partner_invoice_amount_type: partnerInvoiceAmountType,
                collects_commission: p.collects_commission || false,
              };
            } catch (e) {
              console.error('Error processing partner:', p, e);
              return {
                ...p,
                _previousPaymentMode: p.payment_mode || 'FULL_PAYMENT',
                partner_invoice_amount_type: 'FULL',
                collects_commission: false,
              };
            }
          });
        } else {
          console.error('Failed to load partners:', response.status);
          this.partners = [];
        }
      } catch (error) {
        console.error('Error loading partners:', error);
        this.partners = [];
      } finally {
        this.partnersLoading = false;
      }
    },

    async syncPartnersFromChannex() {
      this.partnersSyncing = true;
      try {
        if (!this.calendarId) {
          this.notifyPartner('Calendar ID not found', 'error');
          return;
        }

        const response = await fetch('/partners/api/partners/sync-channex/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          body: JSON.stringify({ calendar_id: this.calendarId }),
        });
        const result = await response.json();
        if (response.ok) {
          this.notifyPartner(
            `Synced ${result.synced} channel(s): ${
              result.channels.join(', ') || 'none'
            }`,
            'success',
          );
          await this.loadPartners();
        } else {
          this.notifyPartner(
            result.error || 'Failed to sync channels',
            'error',
          );
        }
      } catch (error) {
        console.error('Error syncing channels:', error);
        this.notifyPartner('Failed to sync channels', 'error');
      } finally {
        this.partnersSyncing = false;
      }
    },

    async checkStatus() {
      try {
        const response = await fetch(
          `/channex/api/status/?calendar_id=${this.calendarId}`,
        );
        if (!response.ok) {
          console.error('Failed to fetch Channex status:', response.status);
          this.statusChecked = true;
          return;
        }
        const data = await response.json();
        this.channexEnabled = data.channex_enabled;
        this.hasProperty = data.has_property;
        this.showIframe = this.hasProperty;
        this.statusChecked = true;
        if (this.hasProperty) {
          await this.loadIframe();
        }
      } catch (error) {
        console.error('Error checking Channex status:', error);
        this.statusChecked = true;
      }
    },

    connectSyncWebSocket() {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/ws/channex-sync/${this.calendarId}/`;

      this.syncWebSocket = new WebSocket(wsUrl);

      this.syncWebSocket.onopen = () => {
        console.log('Sync WebSocket connected');
      };

      this.syncWebSocket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        console.log('Sync progress update:', data);

        if (data.type === 'sync_progress') {
          this.syncProgress = {
            step: data.step,
            progress: data.progress,
            message: data.message,
            details: data.details || {},
          };

          if (data.step === 'complete') {
            setTimeout(() => {
              this.hasProperty = true;
              this.showIframe = true;
              this.isCreatingProperty = false;
              this.syncProgress = null;
              this.disconnectSyncWebSocket();
              this.loadIframe();
            }, 2000);
          }

          if (data.details && data.details.error) {
            setTimeout(() => {
              this.isCreatingProperty = false;
              this.syncProgress = null;
              this.disconnectSyncWebSocket();
            }, 3000);
          }
        }
      };

      this.syncWebSocket.onerror = (error) => {
        console.error('Sync WebSocket error:', error);
      };

      this.syncWebSocket.onclose = () => {
        console.log('Sync WebSocket disconnected');
      };
    },

    disconnectSyncWebSocket() {
      if (this.syncWebSocket) {
        this.syncWebSocket.close();
        this.syncWebSocket = null;
      }
    },

    async createProperty() {
      if (
        !confirm(
          'Create Channex property and sync all Accommodation Items and Listings?',
        )
      ) {
        return;
      }

      this.isCreatingProperty = true;
      this.syncProgress = {
        step: 'starting',
        progress: 0,
        message: 'Initializing...',
        details: {},
      };

      this.connectSyncWebSocket();

      try {
        const response = await fetch('/channex/api/create-property/', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          body: JSON.stringify({
            calendar_id: this.calendarId,
          }),
        });

        const data = await response.json();
        if (!response.ok) {
          console.error('Failed to create property:', data);
          this.notifyPartner(
            data.error || 'Failed to create property',
            'error',
          );
          this.isCreatingProperty = false;
          this.syncProgress = null;
          this.disconnectSyncWebSocket();
        }
      } catch (error) {
        console.error('Error creating property:', error);
        this.notifyPartner('Failed to create property', 'error');
        this.isCreatingProperty = false;
        this.syncProgress = null;
        this.disconnectSyncWebSocket();
      }
    },

    async loadIframe() {
      if (this.loadingIframe || this.iframeUrl) return;
      this.loadingIframe = true;

      try {
        const response = await fetch('/channex/api/generate-token/', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          body: JSON.stringify({
            calendar_id: this.calendarId,
          }),
        });

        const data = await response.json();

        if (response.ok) {
          this.iframeUrl = `${data.server}/auth/exchange?oauth_session_key=${data.token}&app_mode=headless&redirect_to=/channels&property_id=${data.property_id}&min_stay_type=arrival`;
        } else {
          this.notifyPartner('Failed to generate access token', 'error');
        }
      } catch (error) {
        console.error('Failed to load channel manager:', error);
        this.notifyPartner('Failed to load channel manager', 'error');
      } finally {
        this.loadingIframe = false;
      }
    },

    selectOta(code) {
      if (!code) {
        this.newPartner.name = '';
        this.newPartner.commission_rate = '';
        this.newPartner.booking_source_code = '';
        return;
      }
      const ota = this.otaOptions.find((o) => o.code === code);
      if (ota) {
        this.newPartner.name = ota.name;
        this.newPartner.commission_rate = ota.commission;
        this.newPartner.booking_source_code = ota.code;
      }
    },

    resetNewPartner() {
      this.newPartner = {
        name: '',
        partner_type: 'TRAVEL_AGENCY',
        payment_mode: 'FULL_PAYMENT',
        commission_rate: '',
        booking_source_code: '',
        selected_ota: '',
        invoicing_mode: 'CUSTOMER',
        partner_invoice_amount_type: 'FULL',
        collects_commission: false,
        auto_invoice: false,
        guest_email_policy: 'CONFIRMATION_ONLY',
        notification_email: '',
        guest_email_required: true,
        guest_communication_mode: 'INITIAL_ONLY',
        business_name: '',
        vat_number: '',
        billing_address: '',
        billing_city: '',
        billing_postal_code: '',
        billing_country: '',
        billing_email: '',
      };
      this.showAdvancedPartnerSettings = false;
    },

    async openEditPartner(partner) {
      try {
        await this.fetchPartnerCountries();
        let partnerInvoiceAmountType = (
          partner.partner_invoice_amount_type || 'FULL'
        ).toString();
        if (
          !['FULL', 'FULL_MINUS_COMMISSION'].includes(partnerInvoiceAmountType)
        ) {
          partnerInvoiceAmountType = 'FULL_MINUS_COMMISSION';
        }
        this.editingPartner = {
          ...partner,
          billing_country: partner.billing_country || '',
          partner_invoice_amount_type: partnerInvoiceAmountType,
          collects_commission: partner.collects_commission || false,
          guest_email_policy: partner.guest_email_policy || 'CONFIRMATION_ONLY',
          notification_email: partner.notification_email || '',
          guest_email_required: partner.guest_email_required ?? true,
          guest_communication_mode:
            partner.guest_communication_mode || 'INITIAL_ONLY',
          send_partner_notifications:
            partner.send_partner_notifications || false,
          notify_on_request_created:
            partner.notify_on_request_created !== undefined
              ? partner.notify_on_request_created
              : true,
          notify_on_instant_confirmed:
            partner.notify_on_instant_confirmed !== undefined
              ? partner.notify_on_instant_confirmed
              : true,
          notify_on_request_later_confirmed:
            partner.notify_on_request_later_confirmed !== undefined
              ? partner.notify_on_request_later_confirmed
              : true,
        };
        // Backward compatibility: map deprecated partner-only mode to
        // "no guest emails" + "all guest-targeted emails sent to partner".
        if (
          this.editingPartner.guest_communication_mode === 'PARTNER_ONLY_ALL'
        ) {
          this.editingPartner.guest_email_policy = 'NEVER';
          this.editingPartner.guest_communication_mode =
            'GUEST_AND_PARTNER_ALL';
        }
        this.partnerSaveError = '';
        this.showEditPartnerModal = true;
      } catch (error) {
        console.error('Error opening edit partner modal:', error);
        this.notifyPartner('Failed to open partner editor', 'error');
      }
    },

    closeEditPartnerModal() {
      this.showEditPartnerModal = false;
      this.editingPartner = {};
      this.partnerSaveError = '';
    },

    promptAndCreateAccessCode() {
      if (!this.editingPartner?.id) return;
      const label = window.prompt('Access code label');
      if (label && label.trim()) {
        this.createAccessCode(label.trim());
      }
    },

    promptAndCreateUrlCode() {
      if (!this.editingPartner?.id) return;
      const label = window.prompt('URL code label');
      if (label && label.trim()) {
        this.createUrlCode(label.trim());
      }
    },

    async copyToClipboard(value, successMessage = 'Copied to clipboard') {
      if (!value) return;
      try {
        await navigator.clipboard.writeText(value);
        this._toast(successMessage, 'success');
      } catch (error) {
        console.error('Failed to copy text:', error);
        this._toast('Unable to copy. Please copy manually.', 'danger');
      }
    },

    validatePartnerInvoicingFields(partner) {
      if (partner.invoicing_mode !== 'PARTNER') return null;
      const businessName = (partner.business_name || '').trim();
      const billingEmail = (partner.billing_email || '').trim();
      if (!businessName) {
        return 'Business name is required when invoicing mode is "Invoice Partner".';
      }
      if (!billingEmail) {
        return 'Billing email is required when invoicing mode is "Invoice Partner".';
      }
      return null;
    },

    validatePartnerCommunicationFields(partner) {
      const mode = String(partner.guest_communication_mode || 'INITIAL_ONLY');
      const requiresPartnerEmail = [
        'GUEST_AND_PARTNER_ALL',
        'PARTNER_ONLY_ALL',
      ].includes(mode);
      const notificationEmail = String(partner.notification_email || '').trim();
      if (requiresPartnerEmail && !notificationEmail) {
        return 'Partner notification email is required for this communication mode.';
      }
      return null;
    },
    normalizePartnerCommunicationMode(partner) {
      const notificationEmail = String(partner.notification_email || '').trim();
      if (notificationEmail) return;
      // When no partner email is set, force the hidden/default "no partner emails" mode.
      partner.guest_communication_mode = 'GUEST_ONLY_ALL';
    },

    normalizePartnerUrlNotifications(partner) {
      const notificationEmail = String(partner.notification_email || '').trim();
      if (notificationEmail) return;
      partner.send_partner_notifications = false;
    },

    async saveEditingPartner() {
      if (!this.editingPartner || !this.editingPartner.id) return;
      this.partnerSaveError = '';
      const validationError = this.validatePartnerInvoicingFields(
        this.editingPartner,
      );
      if (validationError) {
        this.partnerSaveError = validationError;
        this.notifyPartner(validationError, 'error');
        return;
      }
      this.normalizePartnerCommunicationMode(this.editingPartner);
      this.normalizePartnerUrlNotifications(this.editingPartner);
      const communicationValidationError =
        this.validatePartnerCommunicationFields(this.editingPartner);
      if (communicationValidationError) {
        this.partnerSaveError = communicationValidationError;
        this.notifyPartner(communicationValidationError, 'error');
        return;
      }
      try {
        const payload = {
          name: this.editingPartner.name,
          commission_rate: this.editingPartner.commission_rate || null,
          payment_mode: this.editingPartner.payment_mode,
          invoicing_mode: this.editingPartner.invoicing_mode,
          partner_invoice_amount_type:
            this.editingPartner.partner_invoice_amount_type,
          collects_commission: this.editingPartner.collects_commission || false,
          auto_invoice: this.editingPartner.auto_invoice,
          guest_email_policy:
            this.editingPartner.guest_email_policy || 'CONFIRMATION_ONLY',
          notification_email: this.editingPartner.notification_email || '',
          guest_email_required: Boolean(
            this.editingPartner.guest_email_required,
          ),
          guest_communication_mode:
            this.editingPartner.guest_communication_mode || 'INITIAL_ONLY',
          send_partner_notifications:
            this.editingPartner.send_partner_notifications || false,
          notify_on_request_created:
            this.editingPartner.notify_on_request_created !== false,
          notify_on_instant_confirmed:
            this.editingPartner.notify_on_instant_confirmed !== false,
          notify_on_request_later_confirmed:
            this.editingPartner.notify_on_request_later_confirmed !== false,
          business_name: this.editingPartner.business_name || '',
          vat_number: this.editingPartner.vat_number || '',
          billing_address: this.editingPartner.billing_address || '',
          billing_city: this.editingPartner.billing_city || '',
          billing_postal_code: this.editingPartner.billing_postal_code || '',
          billing_country: this.editingPartner.billing_country || '',
          billing_email: this.editingPartner.billing_email || '',
        };
        const response = await fetch(
          `/partners/api/partners/${this.editingPartner.id}/`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': this.getCsrfToken(),
            },
            body: JSON.stringify(payload),
          },
        );
        if (response.ok) {
          const index = this.partners.findIndex(
            (p) => p.id === this.editingPartner.id,
          );
          if (index !== -1) {
            this.partners[index] = {
              ...this.partners[index],
              ...this.editingPartner,
            };
          }
          this.closeEditPartnerModal();
          this.notifyPartner('Partner updated', 'success');
        } else {
          const errorData = await response.json();
          const message =
            typeof errorData === 'object' &&
            errorData !== null &&
            !errorData.detail
              ? Object.values(errorData)
                  .flat()
                  .find((m) => typeof m === 'string') ||
                'Failed to update partner'
              : errorData.detail || 'Failed to update partner';
          this.partnerSaveError = message;
          this.notifyPartner(message, 'error');
        }
      } catch (error) {
        console.error('Error updating partner:', error);
        const msg = 'Failed to update partner';
        this.partnerSaveError = msg;
        this.notifyPartner(msg, 'error');
      }
    },

    async createPartner() {
      try {
        if (!this.calendarId) {
          this.notifyPartner('Calendar ID not found', 'error');
          return;
        }
        const validationError = this.validatePartnerInvoicingFields(
          this.newPartner,
        );
        if (validationError) {
          this.notifyPartner(validationError, 'error');
          return;
        }
        this.normalizePartnerCommunicationMode(this.newPartner);
        this.normalizePartnerUrlNotifications(this.newPartner);
        const communicationValidationError =
          this.validatePartnerCommunicationFields(this.newPartner);
        if (communicationValidationError) {
          this.notifyPartner(communicationValidationError, 'error');
          return;
        }

        const payload = {
          calendar_id: this.calendarId,
          name: this.newPartner.name,
          partner_type: this.newPartner.partner_type,
          payment_mode: this.newPartner.payment_mode,
          commission_rate: this.newPartner.commission_rate || null,
          invoicing_mode: this.newPartner.invoicing_mode,
          partner_invoice_amount_type:
            this.newPartner.partner_invoice_amount_type,
          collects_commission: this.newPartner.collects_commission || false,
          auto_invoice: this.newPartner.auto_invoice,
          guest_email_policy:
            this.newPartner.guest_email_policy || 'CONFIRMATION_ONLY',
          notification_email: this.newPartner.notification_email || '',
          guest_email_required: Boolean(this.newPartner.guest_email_required),
          guest_communication_mode:
            this.newPartner.guest_communication_mode || 'INITIAL_ONLY',
          business_name: this.newPartner.business_name || '',
          vat_number: this.newPartner.vat_number || '',
          billing_address: this.newPartner.billing_address || '',
          billing_city: this.newPartner.billing_city || '',
          billing_postal_code: this.newPartner.billing_postal_code || '',
          billing_country: this.newPartner.billing_country || '',
          billing_email: this.newPartner.billing_email || '',
        };

        if (
          this.newPartner.partner_type === 'OTA_CHANNEL' &&
          this.newPartner.booking_source_code
        ) {
          payload.booking_source_code = this.newPartner.booking_source_code;
        }

        const response = await fetch('/partners/api/partners/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          body: JSON.stringify(payload),
        });
        if (response.ok) {
          this.notifyPartner('Partner created', 'success');
          this.showAddPartnerModal = false;
          this.resetNewPartner();
          await this.loadPartners();
        } else {
          const error = await response.json();
          this.notifyPartner(
            error.error ||
              error.booking_source_code?.[0] ||
              'Failed to create partner',
            'error',
          );
        }
      } catch (error) {
        console.error('Error creating partner:', error);
        this.notifyPartner('Failed to create partner', 'error');
      }
    },

    async deletePartner(partner) {
      if (!confirm(`Delete partner '${partner.name}'?`)) return;
      try {
        const response = await fetch(`/partners/api/partners/${partner.id}/`, {
          method: 'DELETE',
          headers: { 'X-CSRFToken': this.getCsrfToken() },
        });
        if (response.ok) {
          this.notifyPartner('Partner deleted', 'success');
          await this.loadPartners();
        } else {
          this.notifyPartner('Failed to delete partner', 'error');
        }
      } catch (error) {
        console.error('Error deleting partner:', error);
        this.notifyPartner('Failed to delete partner', 'error');
      }
    },

    async updatePartner(partner) {
      try {
        const response = await fetch(`/partners/api/partners/${partner.id}/`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          body: JSON.stringify({
            commission_rate: partner.commission_rate || null,
            payment_mode: partner.payment_mode,
          }),
        });
        if (response.ok) {
          this.notifyPartner('Partner updated', 'success');
        } else {
          this.notifyPartner('Failed to update partner', 'error');
        }
      } catch (error) {
        console.error('Error updating partner:', error);
        this.notifyPartner('Failed to update partner', 'error');
      }
    },

    confirmPaymentModeChange(partner, event) {
      const newValue = event.target.value;
      if (newValue !== partner._previousPaymentMode) {
        const modeNames = {
          FULL_PAYMENT: 'Full Payment',
          DEPOSIT: 'Commission as Deposit',
          NO_PAYMENT: 'No Payment',
        };
        if (
          confirm(
            'Change payment mode to ' +
              modeNames[newValue] +
              '?\n\nThis will affect all future bookings from this partner. Existing bookings will not be changed.',
          )
        ) {
          partner._previousPaymentMode = newValue;
          this.updatePartner(partner);
        } else {
          partner.payment_mode = partner._previousPaymentMode;
        }
      }
    },

    async createAccessCode(label) {
      if (!this.editingPartner?.id) return;
      try {
        const response = await fetch(
          `/partners/api/partners/${this.editingPartner.id}/access-codes/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': this.getCsrfToken(),
            },
            body: JSON.stringify({ label }),
          },
        );
        if (response.ok) {
          const data = await response.json();
          if (!this.editingPartner.access_codes) {
            this.editingPartner.access_codes = [];
          }
          this.editingPartner.access_codes.push(data);
          this._toast('Access code created', 'success');
        }
      } catch (error) {
        console.error('Error creating access code:', error);
        this._toast('Failed to create access code', 'danger');
      }
    },

    async toggleAccessCode(codeId) {
      if (!this.editingPartner?.id) return;
      try {
        const response = await fetch(
          `/partners/api/partners/${this.editingPartner.id}/access-codes/${codeId}/toggle/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': this.getCsrfToken(),
            },
          },
        );
        if (response.ok) {
          const data = await response.json();
          const code = this.editingPartner.access_codes.find(
            (c) => c.id === codeId,
          );
          if (code) code.is_active = data.is_active;
          this._toast(
            data.is_active ? 'Code activated' : 'Code deactivated',
            'success',
          );
        }
      } catch (error) {
        console.error('Error toggling access code:', error);
      }
    },

    async regenerateAccessCode(codeId) {
      if (!confirm('Are you sure? The old code will no longer work.')) return;
      if (!this.editingPartner?.id) return;
      try {
        const response = await fetch(
          `/partners/api/partners/${this.editingPartner.id}/access-codes/${codeId}/regenerate/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': this.getCsrfToken(),
            },
          },
        );
        if (response.ok) {
          const data = await response.json();
          const code = this.editingPartner.access_codes.find(
            (c) => c.id === codeId,
          );
          if (code) code.code = data.code;
          this._toast('Code regenerated', 'success');
        }
      } catch (error) {
        console.error('Error regenerating access code:', error);
      }
    },

    async deleteAccessCode(codeId) {
      if (!confirm('Are you sure you want to delete this access code?')) return;
      if (!this.editingPartner?.id) return;
      try {
        const response = await fetch(
          `/partners/api/partners/${this.editingPartner.id}/access-codes/${codeId}/`,
          {
            method: 'DELETE',
            headers: {
              'X-CSRFToken': this.getCsrfToken(),
            },
          },
        );
        if (response.ok) {
          this.editingPartner.access_codes =
            this.editingPartner.access_codes.filter((c) => c.id !== codeId);
          this._toast('Access code deleted', 'success');
        }
      } catch (error) {
        console.error('Error deleting access code:', error);
      }
    },

    async createUrlCode(label) {
      if (!this.editingPartner?.id) return;
      try {
        const response = await fetch(
          `/partners/api/partners/${this.editingPartner.id}/url-codes/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': this.getCsrfToken(),
            },
            body: JSON.stringify({ label }),
          },
        );
        if (response.ok) {
          const data = await response.json();
          if (!this.editingPartner.url_codes) {
            this.editingPartner.url_codes = [];
          }
          this.editingPartner.url_codes.push(data);
          this._toast('URL code created', 'success');
        } else {
          this._toast('Failed to create URL code', 'danger');
        }
      } catch (error) {
        console.error('Error creating URL code:', error);
        this._toast('Failed to create URL code', 'danger');
      }
    },

    async toggleUrlCode(codeId) {
      if (!this.editingPartner?.id) return;
      try {
        const response = await fetch(
          `/partners/api/partners/${this.editingPartner.id}/url-codes/${codeId}/toggle/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': this.getCsrfToken(),
            },
          },
        );
        if (response.ok) {
          const data = await response.json();
          const code = this.editingPartner.url_codes.find(
            (c) => c.id === codeId,
          );
          if (code) code.is_active = data.is_active;
          this._toast(
            data.is_active ? 'URL code activated' : 'URL code deactivated',
            'success',
          );
        }
      } catch (error) {
        console.error('Error toggling URL code:', error);
      }
    },

    async regenerateUrlCode(codeId) {
      if (!confirm('Are you sure? The old URL will no longer work.')) return;
      if (!this.editingPartner?.id) return;
      try {
        const response = await fetch(
          `/partners/api/partners/${this.editingPartner.id}/url-codes/${codeId}/regenerate/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': this.getCsrfToken(),
            },
          },
        );
        if (response.ok) {
          const data = await response.json();
          const code = this.editingPartner.url_codes.find(
            (c) => c.id === codeId,
          );
          if (code) {
            code.code = data.code;
            code.url = data.url;
          }
          this._toast('URL code regenerated', 'success');
        }
      } catch (error) {
        console.error('Error regenerating URL code:', error);
      }
    },

    async deleteUrlCode(codeId) {
      if (!confirm('Are you sure you want to delete this URL code?')) return;
      if (!this.editingPartner?.id) return;
      try {
        const response = await fetch(
          `/partners/api/partners/${this.editingPartner.id}/url-codes/${codeId}/`,
          {
            method: 'DELETE',
            headers: {
              'X-CSRFToken': this.getCsrfToken(),
            },
          },
        );
        if (response.ok) {
          this.editingPartner.url_codes = this.editingPartner.url_codes.filter(
            (c) => c.id !== codeId,
          );
          this._toast('URL code deleted', 'success');
        }
      } catch (error) {
        console.error('Error deleting URL code:', error);
      }
    },
  };

  const store = {};
  Object.defineProperties(store, Object.getOwnPropertyDescriptors(baseStore));
  Object.defineProperties(store, Object.getOwnPropertyDescriptors(customStore));

  return Alpine.store('calendarSettingsChannelsStore', store);
}
